import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const EditDialog = ({ t, self, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({ name: "", email: "", password: "", admin: false });
  useEffect(() => { if (t) setForm({ name: t.name, email: t.email, password: "", admin: t.role === "admin" }); }, [t]);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const save = async () => {
    const body = { name: form.name };
    if (!t.master) {
      body.email = form.email;
      if (form.password) body.password = form.password;
      if (!self) body.role = form.admin ? "admin" : "teacher";
    }
    try {
      await api.patch(`/auth/teachers/${t.id}`, body);
      toast.success(form.password || form.email !== t.email ? "Kaydedildi. Öğretmenin açık oturumları kapatıldı." : "Kaydedildi");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={Boolean(t)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="teacher-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Öğretmeni düzenle</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Ad soyad</Label><Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="teacher-edit-name" /></div>
          {t?.master ? <p className="text-sm text-slate-400">Geliştirici hesabının e-postası, şifresi ve yetkisi sunucu ayarlarından yönetilir.</p> : <>
            <div className="space-y-1.5"><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="teacher-edit-email" /></div>
            <div className="space-y-1.5"><Label>Yeni şifre (boş bırakılırsa değişmez)</Label><Input type="password" value={form.password} onChange={(e) => set({ password: e.target.value })} placeholder="En az 8 karakter" data-testid="teacher-edit-password" /></div>
            {!self && (
              <label className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3 cursor-pointer">
                <span><b>Yönetici öğretmen</b><span className="block text-xs text-slate-400">Kod oluşturur, öğretmenleri yönetir, tüm içeriğe ve analizlere erişir.</span></span>
                <Switch checked={form.admin} onCheckedChange={(v) => set({ admin: v })} data-testid="teacher-edit-admin" />
              </label>
            )}
          </>}
        </div>
        <DialogFooter><Button onClick={save} className="font-bold" data-testid="teacher-edit-save">Kaydet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TeachersPanel = () => {
  const { teacher } = useAuth();
  const [rows, setRows] = useState([]);
  const [removing, setRemoving] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/auth/teachers").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const remove = async (t) => {
    try {
      await api.delete(`/auth/teachers/${t.id}`, { params: { with_content: true } });
      toast.success("Öğretmen ve oluşturdukları silindi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="teachers-panel">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6 text-[#FACC15]" />Öğretmen hesapları</h2>
      {rows.map((t) => (
        <div key={t.id} className="panel p-4 flex flex-wrap items-center gap-4" data-testid={`teacher-row-${t.id}`}>
          <div className="flex-1 min-w-[220px]">
            <p className="font-semibold flex items-center gap-2 flex-wrap"><span className={`h-2.5 w-2.5 rounded-full ${t.online ? "bg-[#22C55E]" : "bg-slate-600"}`} title={t.online ? "Çevrim içi" : "Çevrim dışı"} data-testid={`teacher-online-${t.id}`} />{t.name} {t.role === "admin" && <span className="ml-1 text-xs rounded-full bg-[#FACC15] text-[#1A1405] px-2 py-0.5 font-bold" data-testid={`teacher-admin-badge-${t.id}`}>{t.master ? "Geliştirici" : "Yönetici"}</span>}{t.academic && <span className="text-xs rounded-full bg-sky-500/20 text-sky-300 px-2 py-0.5 font-bold" data-testid={`teacher-academic-badge-${t.id}`}>Akademisyen</span>}</p>
            <p className="text-sm text-slate-400">{t.email} · {t.pools} havuz · {t.activities} etkinlik · {t.rooms} oyun · {t.online ? <span className="text-[#4ADE80]">çevrim içi</span> : t.last_seen ? `son görülme ${new Date(t.last_seen).toLocaleString("tr-TR")}` : "hiç giriş yapmadı"}</p>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={Boolean(t.academic)} onCheckedChange={(v) => api.put(`/auth/teachers/${t.id}/academic`, { academic: v }).then(load).catch((e) => toast.error(errMsg(e)))} data-testid={`teacher-academic-${t.id}`} />Akademisyen</label>
          {teacher?.developer && <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`teacher-edit-${t.id}`}><Pencil className="h-4 w-4 mr-1" />Düzenle</Button>}
          {teacher?.developer && t.role !== "admin" && (
            <Button size="sm" variant="ghost" className="text-red-400" onClick={() => setRemoving(t)} data-testid={`teacher-delete-${t.id}`}><Trash2 className="h-4 w-4 mr-1" />Sil</Button>
          )}
        </div>
      ))}
      <EditDialog t={editing} self={editing?.id === teacher?.id} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
      <ConfirmDialog open={Boolean(removing)} onOpenChange={(o) => !o && setRemoving(null)} title="Öğretmeni sil"
        description={`${removing?.name || ""} hesabı silinsin mi? Onaylarsanız oluşturduğu havuzlar ve etkinlikler de silinir. Oyunlar listede kalır, isterseniz Oyunlar sayfasından ayrıca silebilirsiniz.`}
        confirmText="Hesabı ve içeriklerini sil" onConfirm={() => { remove(removing); setRemoving(null); }} />
    </div>
  );
};
